/*
 OK FBI — Secure Access Gate
 The attached background image already contains the OK FBI identity
 (crest + INVESTIGATION GAME + AUTHORIZED ACCESS ONLY). We overlay
 EXACTLY one small login panel — no titles, no feature cards, no footer text.
*/
import React, { useEffect, useState, useContext, createContext } from "react";
import { Navigate, useLocation, useNavigate, Link } from "react-router-dom";
import { api, formatErr, BASE_URL } from "@/lib/api";
import { Lock, LogOut } from "lucide-react";

// -------- Current user context (client-side) --------
const AuthCtx = createContext({ user: null, refresh: () => {}, hasPerm: () => false, isOwner: false });
export function useAuth() { return useContext(AuthCtx); }

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data?.authenticated ? data.user : null);
    } catch { setUser(null); }
    finally { setLoaded(true); }
  };
  useEffect(() => { refresh(); }, []);
  const hasPerm = (key) => !!(user?.permissions?.[key]);
  const isOwner = user?.role === "OWNER";
  return (
    <AuthCtx.Provider value={{ user, refresh, hasPerm, isOwner, loaded }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function LoginPage() {
  const [identifier, setIdentifier] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [ttConfigured, setTtConfigured] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const searchParams = new URLSearchParams(loc.search);
  const rawNext = searchParams.get("next") || "/app";
  const next = (rawNext.startsWith("/") && !rawNext.startsWith("//")) ? rawNext : "/app";
  const isTiktokPending = searchParams.get("tiktok") === "pending";
  const auth = useAuth();

  useEffect(() => {
    let stop = false;
    api.get("/auth/tiktok/config")
      .then(({ data }) => {
        if (!stop && data?.configured) setTtConfigured(true);
      })
      .catch(() => {
        if (!stop) setTtConfigured(false);
      });
    return () => { stop = true; };
  }, []);

  const apiBase = (BASE_URL || "").replace(/\/+$/, "");
  const tiktokStartUrl = `${apiBase}/api/auth/tiktok/start?account_type=INVESTIGATOR`;

  const submitLogin = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const res = await api.post("/auth/login", { password: pw, identifier: identifier.trim() || null });
      if (res?.data?.token) localStorage.setItem("okfbi_token", res.data.token);
      await auth.refresh();
      nav(next, { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(formatErr(detail) || "بيانات الدخول غير صحيحة");
    } finally { setBusy(false); }
  };

  return (
    <div dir="rtl" className="relative min-h-screen w-full overflow-hidden text-white bg-black" data-testid="login-page">
      <div className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/auth/login-bg.png')" }} aria-hidden />
      <div className="relative z-10 min-h-screen flex items-end justify-center pb-[8vh] px-4">
        <div className="w-[92%] max-w-[390px]">
          <div
            style={{
              background: "linear-gradient(180deg, rgba(10,10,10,0.88) 0%, rgba(4,4,4,0.92) 100%)",
              border: "1px solid rgba(197,160,89,0.42)",
              boxShadow: "0 24px 60px rgba(0,0,0,0.85), inset 0 0 1px rgba(197,160,89,0.35)",
              backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
              padding: "18px 20px", borderRadius: "2px",
            }}>
            {isTiktokPending && (
              <div
                data-testid="tiktok-pending-notice"
                className="mb-3 p-2.5 border border-amber-500/40 bg-amber-950/40 text-amber-200 text-[11px] font-cairo text-center leading-relaxed"
                style={{ borderRadius: "2px" }}
              >
                تم التحقق من حساب TikTok بنجاح. حساب الستريمر قيد المراجعة وبانتظار موافقة الإدارة قبل تفعيل الدخول.
              </div>
            )}
            <form onSubmit={submitLogin}>
              <label className="block text-[11px] text-zinc-300 font-cairo mb-1">Agent ID / اسم المستخدم</label>
              <div className="flex items-center gap-2 border border-[#3a3a3a] focus-within:border-[#c5a059] bg-black/60 px-2 py-2 mb-3">
                <span className="text-zinc-500 font-mono-ar text-[11px]">#</span>
                <input value={identifier}
                  onChange={(e) => setIdentifier(e.target.value.slice(0, 80))}
                  data-testid="login-agent-id" type="text" placeholder="Agent ID أو اسم المستخدم"
                  className="w-full bg-transparent outline-none text-white text-[13px] font-mono-ar tracking-widest" autoFocus />
              </div>
              <label className="block text-[11px] text-zinc-300 font-cairo mb-1">كلمة المرور</label>
              <div className="flex items-center gap-2 border border-[#3a3a3a] focus-within:border-[#c5a059] bg-black/60 px-2 py-2">
                <Lock size={13} className="text-zinc-500" />
                <input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                  data-testid="login-password" placeholder="••••••••"
                  className="flex-1 min-w-0 bg-transparent outline-none text-white text-[13px] font-mono-ar tracking-wider" />
              </div>
              {err && <div className="mt-3 text-[11px] text-red-300 font-cairo" data-testid="login-error">{err}</div>}
              <button type="submit" disabled={busy || !pw || !identifier.trim()} data-testid="login-submit"
                className="w-full mt-4 py-2.5 text-[13px] font-cairo tracking-[0.35em] disabled:opacity-50"
                style={{ background: busy ? "#8a7038" : "linear-gradient(180deg, #d9b060 0%, #a4813f 100%)",
                  color: "#0a0a0a", border: "1px solid #d9b060",
                  boxShadow: "0 6px 22px rgba(197,160,89,0.35)" }}>
                {busy ? "..." : "دخول"}
              </button>
            </form>

            {/* --- TikTok registration for new investigators --- */}
            <div className="mt-3 pt-3 border-t border-zinc-800/60">
              <div className="text-[10px] text-zinc-500 font-cairo text-center mb-2">محقق جديد؟</div>
              {ttConfigured ? (
                <a
                  href={tiktokStartUrl}
                  data-testid="tiktok-register-btn"
                  className="w-full py-2.5 border border-[#c5a059]/50 hover:border-[#c5a059] text-[#c5a059] hover:bg-[#c5a059]/10 text-[12px] font-cairo tracking-[0.15em] transition-colors flex items-center justify-center gap-2 bg-black/40"
                  style={{ borderRadius: "2px" }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.4a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.3a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V9.05a8.16 8.16 0 004.76 1.52v-3.4a4.85 4.85 0 01-1-.48z"/>
                  </svg>
                  <span>تسجيل محقق جديد عبر TikTok</span>
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => setErr("تسجيل المحققين عبر TikTok يتطلب ضبط مفاتيح TIKTOK_CLIENT_KEY و TIKTOK_CLIENT_SECRET في ملف backend/.env")}
                  data-testid="tiktok-register-btn"
                  className="w-full py-2.5 border border-[#c5a059]/50 hover:border-[#c5a059] text-[#c5a059] hover:bg-[#c5a059]/10 text-[12px] font-cairo tracking-[0.15em] transition-colors flex items-center justify-center gap-2 bg-black/40 cursor-pointer"
                  style={{ borderRadius: "2px" }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.4a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.3a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V9.05a8.16 8.16 0 004.76 1.52v-3.4a4.85 4.85 0 01-1-.48z"/>
                  </svg>
                  <span>تسجيل محقق جديد عبر TikTok</span>
                </button>
              )}
            </div>

            {/* --- Squad Guest entry: the join page collects session code + temporary name --- */}
            <Link to="/squad/join" data-testid="open-squad-join"
              className="w-full mt-2 py-2.5 border border-gold/40 text-gold hover:bg-gold/10 text-[12px] font-cairo tracking-[0.2em] transition-colors flex items-center justify-center gap-2 bg-black/40">
              <span>دخول Squad بالاسم ورقم الجلسة</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Public legal links remain visible without auth; provider routes stay server-side. */}
      <div className="absolute bottom-3 inset-x-0 z-20 flex flex-col items-center gap-1 pointer-events-none" data-testid="login-legal-links">
        <div className="flex items-center gap-3 text-[10px] font-cairo tracking-[0.25em] pointer-events-auto">
          <Link to="/terms" className="text-zinc-400 hover:text-gold transition-colors" data-testid="login-terms-link">شروط الاستخدام</Link>
          <span className="text-zinc-700">·</span>
          <Link to="/privacy" className="text-zinc-400 hover:text-gold transition-colors" data-testid="login-privacy-link">سياسة الخصوصية</Link>
        </div>
        <div className="text-[9px] text-zinc-600 font-cairo tracking-wider max-w-[560px] text-center leading-relaxed pointer-events-none px-4">
          OK FBI لعبة تحقيق تفاعلية خيالية لأغراض الترفيه — غير مرتبطة بمكتب التحقيقات الفيدرالي (FBI) أو أي جهة رسمية
        </div>
      </div>
    </div>
  );
}

export function RequireAuth({ children }) {
  const [state, setState] = useState({ checked: false, ok: false });
  const location = useLocation();
  const auth = useAuth();
  useEffect(() => {
    let stop = false;
    api.get("/auth/me")
      .then(({ data }) => { if (!stop) setState({ checked: true, ok: !!data?.authenticated }); })
      .catch(() => { if (!stop) setState({ checked: true, ok: false }); });
    return () => { stop = true; };
  }, [location.pathname]);
  if (!state.checked) {
    return <div dir="rtl" className="min-h-screen bg-black flex items-center justify-center text-gold text-[10px] tracking-[0.35em] font-mono-ar">جاري التحقق من الجلسة...</div>;
  }
  if (!state.ok) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return children;
}

/** Guard a route by a specific permission key. Fails closed (redirects home). */
export function RequirePerm({ perm, children }) {
  const { user, loaded } = useAuth();
  if (!loaded) return <div dir="rtl" className="min-h-screen bg-black flex items-center justify-center text-gold text-[10px] tracking-[0.35em] font-mono-ar">جاري التحقق...</div>;
  if (user?.role === "OWNER") return children;
  if (user && (user.permissions || {})[perm]) return children;
  return <Navigate to="/app" replace />;
}

export function HostLogoutButton({ compact = false }) {
  const nav = useNavigate();
  const auth = useAuth();
  const doLogout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("okfbi_token");
    try { await auth.refresh(); } catch {}
    nav("/login", { replace: true });
  };
  return (
    <button onClick={doLogout} data-testid="host-logout"
      title="تسجيل الخروج والتبديل لحساب آخر"
      className="flex items-center gap-1.5 border border-red-900/60 bg-red-950/30 text-red-300 hover:bg-red-950/60 hover:border-red-600 px-2.5 py-1 rounded text-xs font-cairo transition-colors cursor-pointer">
      <LogOut size={12} />
      <span>تسجيل خروج</span>
    </button>
  );
}
